import React from 'react';
import '../styles/pages/Legal.css';

const Terms: React.FC = () => {
  return (
    <div className="legal-page-container">
      <div className="legal-card">
        <header className="legal-header">
          <h1>Terms of Service</h1>
          <p>Effective: September 9, 2025</p>
        </header>

        <section className="legal-section">
          <p>
            Welcome to Overture, Inc. (“Overture,” “we,” “us,” “our”). By enrolling your child (“Student”) in our after-school programs or otherwise using our services (the “Services”), you agree to these Terms of Service (“Terms”). If you do not agree, please do not enroll your Student or use our Services.
          </p>
          <p>
            We may update these Terms occasionally. If we make material changes, we’ll provide notice (e.g., email or SMS messages) so you can review them. Your continued use of the Services after changes become effective indicates acceptance of the revised Terms.
          </p>
          <p>
            If you have questions about these Terms or otherwise wish to contact us, please reach out via email at <a href="mailto:info@overture.games">info@overture.games</a>.
          </p>
        </section>

        <section className="legal-section">
          <h2>1. Who Can Enroll</h2>
          <h3>1.1 Legal Authority</h3>
          <p>
            You confirm that you are the legal parent or guardian of the Student and have the authority to enroll them. By enrolling your Student, you agree you are responsible for compliance with these Terms on the Student’s behalf.
          </p>
          <h3>1.2 Children Under 13 (COPPA)</h3>
          <p>
            We collect personal information from children under 13 only after parental consent is provided, as required by the Children’s Online Privacy Protection Act (COPPA).
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Our Programs</h2>
          <h3>2.1 What We Do</h3>
          <p>
            We partner with schools, community organizations, and families to enrich student learning beyond regular school hours. Our services may include direct instruction, digital resources, and interactive lessons. We also use a digital platform to share updates, track progress, and send communications to parents.
          </p>
          <h3>2.2 Signing Up</h3>
          <p>
            To register a Student, you must submit accurate information (e.g., name, school, grade, and any relevant health details). This helps us create a safe and tailored educational environment.
          </p>
          <h3>2.3 Changes to Services</h3>
          <p>
            We may update or adjust our curriculum, schedule, or features at any time. If those changes significantly affect your Student’s experience, we’ll do our best to let you know in advance.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. Payment and Fees</h2>
          <h3>3.1 Tuition and Fees</h3>
          <p>
            All tuition or fees will be clearly disclosed during enrollment. If you enroll through us, we work with a secure third-party payment processor (e.g., Stripe) to handle billing.
          </p>
          <h3>3.2 Refunds</h3>
          <p>
            Our refund policy varies by program. Contact us if you have questions regarding withdrawals, missed classes, or cancellations.
          </p>
          <h3>3.3 Late or Failed Payments</h3>
          <p>
            If payments aren’t made on time, we reserve the right to pause or cancel your Student’s enrollment until any outstanding amounts are resolved.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. Privacy &amp; Your Data</h2>
          <h3>4.1 Privacy &amp; Data Pledge</h3>
          <p>
            We care deeply about how we collect, store, and use personal information, especially for children. Our Privacy &amp; Data Pledge explains what data we collect, how we use it, and how we protect it. By enrolling your Student, you acknowledge reading and agreeing to that pledge.
          </p>
          <h3>4.2 Student Information</h3>
          <p>
            We collect basic details, such as names, class activities, and photos of your Student’s projects to share updates, evaluate teaching methods, and keep you informed.
          </p>
          <h3>4.3 Audio Recordings</h3>
          <p>
            By enrolling, you acknowledge that we may capture classroom audio for teacher professional development.
          </p>
          <h3>4.4 Data Security</h3>
          <p>
            We take reasonable measures to maintain the confidentiality and security of all Student data, adhering to the latest data security standards and other applicable data protection practices. We will respond promptly to data breaches and notify affected parties, as required by law.
          </p>
          <h3>4.5 FERPA (School-Based Programs)</h3>
          <p>
            The partnering school or district represents that it has obtained all necessary parental consents or otherwise has authority under FERPA to share student records with Overture. We act solely as a “school official” to provide educational services, and we will not use or disclose student records for any non-educational purpose without the school’s explicit direction.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Acceptable Use</h2>
          <h3>5.1 Appropriate Use</h3>
          <p>
            You agree not to misuse our services or interfere with anyone else’s ability to enjoy them. That includes not entering false information or circumventing our security measures.
          </p>
          <h3>5.2 Student Conduct</h3>
          <p>
            We aim to create an inclusive, safe environment. Harassment, bullying, or disruptive behavior by any participant (Student, teacher, etc.) is not allowed. If issues persist, we may remove the Student from the program to protect everyone involved.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Intellectual Property</h2>
          <h3>6.1 Our Materials</h3>
          <p>
            All curriculum, content, and software are owned or licensed by Overture. You and your Student may use these only for educational purposes.
          </p>
          <h3>6.2 No Unauthorized Use</h3>
          <p>
            Please do not copy, distribute, reverse engineer, or create derivative works from our materials without our written consent.
          </p>
          <h3>6.3 Feedback</h3>
          <p>
            We welcome ideas to improve our programs. By sharing feedback, you grant us permission to use it without compensation.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Safety and Disclaimers</h2>
          <h3>7.1 Child Safety</h3>
          <p>
            Although we take steps to ensure a secure environment, we are not a licensed childcare facility. Each partner school or facility may have its own safety policies, which you must follow.
          </p>
          <h3>7.2 No Warranties</h3>
          <p>
            We provide the Services “as is,” without warranties of any kind. While we strive for quality, we cannot guarantee error-free operation or continuous availability.
          </p>
          <h3>7.3 Liability Limits</h3>
          <p>
            To the fullest extent allowed by law, we’re not responsible for indirect, incidental, or consequential damages (such as lost revenue). Our total liability is limited to the amount of fees you paid for the applicable enrollment period.
          </p>
          <h3>7.4 No Sale of Children’s Data</h3>
          <p>
            We do not sell personal data, including identifiable student or child data. This aligns with COPPA, FERPA, CPRA, and other child privacy laws.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Overture, its officers, employees, and affiliates from any claims, damages, or liabilities arising from your or your Student’s misuse of the Services or violation of these Terms.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Enrollment Changes</h2>
          <h3>9.1 Our Right to End</h3>
          <p>
            We may end or suspend enrollment if you or your Student breaches these Terms or if we discontinue a program.
          </p>
          <h3>9.2 Your Right to End</h3>
          <p>
            You can withdraw your Student at any time, subject to our refund policy for your student’s program.
          </p>
          <h3>9.3 Post-Termination</h3>
          <p>
            Provisions regarding intellectual property, liability limits, indemnification, and dispute resolution remain in effect even after enrollment ends.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Governing Law and Dispute Resolution</h2>
          <h3>10.1 Choice of Law</h3>
          <p>
            These Terms are governed by Delaware law, without regard to conflict-of-law rules.
          </p>
          <h3>10.2 Arbitration</h3>
          <p>
            If a dispute arises that we can’t resolve informally, we agree to final, binding arbitration administered by JAMS, under its applicable rules. Each party is responsible for its own arbitration costs unless otherwise awarded by the arbitrator. An arbitration award may be entered in any court with jurisdiction.
          </p>
          <h3>10.3 Class Action Waiver</h3>
          <p>
            Except where prohibited by law, each party waives the right to participate in any class, collective, or representative action. An arbitrator shall not consolidate the claims of more than one individual nor preside over any class, collective, or representative proceeding.
          </p>
          <h3>10.4 Exceptions</h3>
          <p>
            We may seek an injunction or other immediate legal remedies to protect our intellectual property or if there’s a threat to children’s safety.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Additional Info</h2>
          <h3>11.1 Entire Agreement</h3>
          <p>
            These Terms, together with our Privacy &amp; Data Pledge and any additional written enrollment agreements, constitute the entire agreement between you and Overture regarding the Services.
          </p>
          <h3>11.2 Severability</h3>
          <p>
            If any provision of these Terms is held invalid or unenforceable, the remaining provisions shall remain in full force and effect.
          </p>
          <h3>11.3 Waiver</h3>
          <p>
            The failure of either party to enforce any right or provision of these Terms will not constitute a waiver of future enforcement of that right or provision.
          </p>
          <h3>11.4 Assignment</h3>
          <p>
            You may not assign or transfer these Terms without our prior written consent. We may assign these Terms in connection with a merger, acquisition, or sale of assets.
          </p>
        </section>

        <header className="legal-header" style={{ marginTop: '2rem' }}>
          <h1>Privacy &amp; Data Pledge</h1>
          <p>Effective: September 9, 2025</p>
        </header>

        <section className="legal-section">
          <p>
            At Overture (“Overture,” “we,” “us,” “our”), we’re dedicated to providing engaging after-school experiences for students—both in-person on school campuses and online via our platform. We follow relevant privacy laws (e.g., COPPA, FERPA, CPRA) to safeguard any data we handle. This Privacy &amp; Data Pledge explains in further detail what personal information we collect, how we use it, and the commitments we make to protect it.
          </p>
          <p>
            We may update this Pledge occasionally. If we make material changes, we’ll provide notice so you can review them and ask questions before the updated policy applies.
          </p>
          <p>
            If you have questions or otherwise wish to contact us, please reach out at <a href="mailto:info@overture.games">info@overture.games</a>.
          </p>
        </section>

        <section className="legal-section">
          <h2>1. What We Do</h2>
          <p>
            We partner with schools, community organizations, and families to enrich student learning beyond regular school hours. Our services may include direct instruction, digital resources, and interactive lessons. We also use a digital platform to share updates, track progress, and send communications to parents.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. What We Collect &amp; Why</h2>
          <h3>2.1 Student and Guardian Information</h3>
          <p>
            For our in-person programs, we collect student and guardian information (e.g. name, grade level, school, and relevant health or allergy details) to ensure safe, tailored instruction. We track attendance, lesson completion, and progress metrics to personalize the learning experience and keep parents informed. We also collect photos, videos and audio of students’ and their projects so that parents can see their child’s progress.
          </p>
          <h3>2.2 Class Recordings</h3>
          <p>
            We may record teacher audio during class to help teachers refine their instructional methods. Although the recording is focused on the teacher’s voice, it might pick up background audio from students or parents. These recordings do not identify specific students or parents, and we do not share them outside Overture.
          </p>
          <h3>2.3 Online Portal Accounts</h3>
          <p>
            To enroll or use our online portal, parents or guardians provide their names, emails, and payment details (handled by a secure third party) to manage billing and account access.
          </p>
          <h3>2.4 Marketing &amp; Promotional Use</h3>
          <p>
            On rare occasions, we may request to use photos of students’ classroom activities in our promotional materials. We will only do this after obtaining written consent from a parent or legal guardian.
          </p>
          <h3>2.5 Minimized Data Use</h3>
          <p>
            We only collect and retain personal information needed for authorized educational purposes.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. How We Use Your Data</h2>
          <h3>3.1 Educational Enhancement</h3>
          <p>
            We use the information above to enroll students, customize lessons, and share updates with parents/guardians.
          </p>
          <h3>3.2 No Sales</h3>
          <p>
            We never sell personal information or personally identifiable student data.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. Data Retention, Security, and Deletion</h2>
          <h3>4.1 Student Records</h3>
          <p>
            Held for the duration of the program plus any legally required period (e.g., up to two years after enrollment ends), then securely deleted or anonymized.
          </p>
          <h3>4.2 Teacher &amp; School Data</h3>
          <p>
            Retained as necessary for class coordination and improvements, or as required by law or contract.
          </p>
          <h3>4.3 Security Program</h3>
          <p>
            We maintain reasonable security safeguards (administrative, technical, physical) to prevent unauthorized access or disclosure.
          </p>
          <h3>4.4 Deletion Requests</h3>
          <p>
            Parents, guardians, or teachers may request deletion of specific records where permissible. We will promptly honor valid requests, except where we have a legal or contractual obligation to retain the data.
          </p>
          <p>
            If you have questions or otherwise wish to contact us, please reach out via email at <a href="mailto:info@overture.games">info@overture.games</a>.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Terms;
